import React, { useCallback, useMemo } from 'react';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import ButtonComponent from 'components/button/buttonComponent';
import CustomRangeDatepicker from 'components/react-datapicker/CustomRangeDatepicker';
import Select, {
  PropsValue,
  components,
  OptionProps,
  SingleValueProps,
  MultiValueRemoveProps,
  MultiValueProps,
} from 'react-select';
import {
  DropdownIndicator,
  DropdownIndicatorSingle,
  IMultiValueLabel,
  ISingleValueLabel,
  NullIndicatorSeparator,
} from 'components/dropdown/ReactSelectComponents';
import { AppointmentTypeEnum } from 'graphql/generated/hasura';
import {
  appointmentRenderInfo,
  AppointmentStatusFilterOptions,
  HistoricAppointmentsFilter,
} from '../../interfaces';
import { historyStatuses } from '../AppointmentHistory';
import { FILTER_BLOCK_ID } from './tableKeyMap';
import { useMyAppointmentsLocale } from '../../pages/MyAppointments';
import { capitalizeFirstLetter } from '../../../../utilities/functions';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import TableFilterPopupWrapper from '../../../../components/TableFilterPopupWrapper';

const SingleValue = (props: SingleValueProps<ISingleValueLabel>) => {
  return (
    <components.SingleValue
      {...props}
      innerProps={{
        className: '!text-sm font-medium !text-neutral-800',
      }}
    >
      {props.children}
    </components.SingleValue>
  );
};

const Option = (props: OptionProps<ISingleValueLabel>) => {
  return (
    <components.Option
      {...props}
      className={twMerge(
        '!text-base font-semibold !text-neutral-800',
        props.isSelected && '!text-white !bg-clc-blue',
      )}
    >
      {props.children}
    </components.Option>
  );
};

const OptionMulti = (props: OptionProps<IMultiValueLabel>) => {
  return (
    <components.Option
      {...props}
      className={twMerge(
        '!text-base font-semibold !text-neutral-800',
        props.isSelected && '!text-white !bg-clc-blue',
      )}
    >
      {props.children}
    </components.Option>
  );
};

const MultiValueRemove = (
  props: MultiValueRemoveProps<IMultiValueLabel, true>,
) => {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{ className: 'hidden' }}
    />
  );
};

const MultiValueContainer = (
  props: MultiValueProps<IMultiValueLabel, true>,
) => {
  const { getValue, index, options } = props;
  const selectedCount = getValue().length;

  const isAllSelected =
    getValue().filter(({ value }) => !!value).length !== selectedCount;
  if (index !== 0) {
    return null;
  }
  return (
    <components.MultiValue
      {...props}
      innerProps={{
        className: 'absolute !px-0 w-full !bg-transparent font-medium',
      }}
      className="!px-0 !text-sm !text-neutral-800"
    >
      {!isAllSelected ? `${selectedCount} selected` : options[0].label}
    </components.MultiValue>
  );
};

interface IPastAppointmentFilter {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleFilterChange: (form: IFilterForm) => void;
  defaultFilter: HistoricAppointmentsFilter;
}

export interface IFilterForm {
  from?: string;
  to?: string;
  type?: PropsValue<ISingleValueLabel>;
  appointmentStatus?: PropsValue<IMultiValueLabel>;
}

const PastAppointmentFilter: React.FC<IPastAppointmentFilter> = ({
  open,
  setOpen,
  handleFilterChange,
  defaultFilter,
}) => {
  const { locale, loading } = useMyAppointmentsLocale();

  const typeOptions = useMemo(
    () =>
      [
        {
          value: undefined,
          label: locale.all,
        },
        {
          value: AppointmentTypeEnum.Chat,
          label: appointmentRenderInfo[AppointmentTypeEnum.Chat].label,
        },
        {
          value: AppointmentTypeEnum.Video,
          label: appointmentRenderInfo[AppointmentTypeEnum.Video].label,
        },
        {
          value: AppointmentTypeEnum.Dnavisit,
          label: 'DNA Visit',
        },
      ] as ISingleValueLabel[],
    [locale],
  );

  const statusOptions = useMemo(
    () =>
      [
        {
          value: undefined,
          label: locale.all,
        },
        ...historyStatuses.map((status) => ({
          value: status,
          label: capitalizeFirstLetter(status),
        })),
      ] as IMultiValueLabel[],
    [locale],
  );

  const [form, setForm] = React.useState<IFilterForm>(() => {
    const { from, to, type, appointmentStatus = [] } = defaultFilter;
    const formStatus = statusOptions.filter(({ value }) =>
      appointmentStatus.includes(value as AppointmentStatusFilterOptions),
    );

    const defaultStatuses = statusOptions.filter(({ value }) => !!value);
    const isAllIncludes = defaultStatuses.length === formStatus.length;

    const typeStatus = typeOptions.find(({ value }) => value === type);
    return {
      from,
      type: type ? typeStatus : undefined,
      appointmentStatus: isAllIncludes ? undefined : formStatus,
      to,
    } as IFilterForm;
  });
  const closeOptions = useCallback(() => setOpen(false), [setOpen]);
  const container = document.getElementById(FILTER_BLOCK_ID);

  const onDateChange = (from: string | undefined, to: string | undefined) => {
    const toDate = to ? moment(to).endOf('day').toISOString() : undefined;
    setForm((form) => ({ ...form, from, to: toDate }));
  };

  const onTypeChange = (type: ISingleValueLabel) => {
    setForm((form) => ({ ...form, type }));
  };

  const onStatusChange = (appointmentStatusOptions: IMultiValueLabel[]) => {
    let appointmentStatus: IMultiValueLabel[] = appointmentStatusOptions;
    const isAllIncludesInOldStatuses = (
      (form.appointmentStatus as IMultiValueLabel[]) || []
    )
      .map(({ value }) => value)
      .filter((value) => !value).length;

    const statuses = appointmentStatusOptions.filter(({ value }) => !!value);
    const defaultStatuses = statusOptions.filter(({ value }) => !!value);

    const isAllIncludes =
      !!appointmentStatusOptions
        .map(({ value }) => value)
        .filter((value) => !value).length ||
      defaultStatuses.length === statuses.length;

    if (isAllIncludes && !isAllIncludesInOldStatuses) {
      appointmentStatus = statusOptions;
    }

    if (isAllIncludes && isAllIncludesInOldStatuses) {
      appointmentStatus = [];
    }

    if (
      isAllIncludes &&
      isAllIncludesInOldStatuses &&
      defaultStatuses.length !== statuses.length
    ) {
      appointmentStatus = statuses;
    }

    setForm((form) => ({ ...form, appointmentStatus }));
  };

  const onReset = () => {
    handleFilterChange({});
    setForm({});
    closeOptions();
  };

  const onApply = () => {
    handleFilterChange(form);
    closeOptions();
  };

  if (!open || !container || !locale || loading) {
    return null;
  }
  return (
    <TableFilterPopupWrapper
      open={open}
      container={container}
      closeOptions={closeOptions}
      className="-right-[26px] desktop:top-24"
    >
      <div className="p-5 text-neutral-800 flex items-center justify-between border border-transparent border-b-gray-opacity-15">
        <div className="font-semibold">{locale.filterBy}</div>
        <ButtonComponent
          type="icon"
          testID={'icon-close'}
          Icon={Close}
          onClick={closeOptions}
          className="!p-0 !h-auto !w-auto hover:!bg-light-teal"
          iconWidth="!stroke-med-gray-3 !w-[20px] !h-[20px]"
        />
      </div>

      <div className="p-5 text-neutral-800 flex flex-col justify-between gap-6 mb-2">
        <div className="flex flex-col gap-0.5">
          <div className="font-semibold">{locale.dateRange}</div>
          <CustomRangeDatepicker
            onDateChange={onDateChange}
            start={form?.from}
            end={form?.to}
          />
        </div>
        <div className="flex flex-col gap-0.5">
          <div className="font-semibold">{locale.type}</div>
          <Select
            value={form?.type}
            options={typeOptions}
            onChange={(type) =>
              onTypeChange(type as unknown as ISingleValueLabel)
            }
            className="dropdown"
            classNamePrefix="react-select"
            isSearchable={false}
            placeholder={
              <span className="text-sm text-med-gray-3 font-medium">
                {typeOptions[0].label}
              </span>
            }
            components={{
              DropdownIndicator: DropdownIndicatorSingle,
              IndicatorSeparator: NullIndicatorSeparator,
              SingleValue,
              Option,
            }}
          />
        </div>
        <div className="flex flex-col gap-0.5">
          <div className="font-semibold">{locale?.status}</div>
          <Select
            value={form?.appointmentStatus}
            onChange={(status) =>
              onStatusChange(status as unknown as IMultiValueLabel[])
            }
            defaultValue={form?.appointmentStatus}
            options={statusOptions}
            components={{
              MultiValue: MultiValueContainer,
              DropdownIndicator,
              IndicatorSeparator: NullIndicatorSeparator,
              MultiValueRemove,
              Option: OptionMulti,
            }}
            isMulti
            isClearable={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isSearchable={false}
            blurInputOnSelect={false}
            placeholder={
              <span className="text-sm text-med-gray-3 font-medium">
                {statusOptions[0].label}
              </span>
            }
            className="dropdown"
            classNamePrefix="react-select"
            closeMenuOnScroll
          />
        </div>
      </div>

      <div className="p-5 mt-auto text-neutral-800 flex items-center justify-between border border-transparent border-t-gray-opacity-15">
        <ButtonComponent
          onClick={onReset}
          className="!text-sm"
          type="underline"
          testID={'reset'}
        >
          {locale.reset}
        </ButtonComponent>
        <ButtonComponent
          className="!text-sm"
          onClick={onApply}
          testID={'apply'}
        >
          {locale.apply}
        </ButtonComponent>
      </div>
    </TableFilterPopupWrapper>
  );
};

export default PastAppointmentFilter;
